import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { ModelCard } from "./ModelCard";
import { PriceDialog } from "./PriceDialog";
import { VideoSimulator } from "./VideoSimulator";
import { RefLimitsCard } from "./RefLimitsCard";
import { QUALITY } from "./llmUtil";

const KINDS = [["", "Semua"], ["image", "Gambar"], ["video", "Video"]];

export default function MediaModelsTab() {
  const [kind, setKind] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const [probes, setProbes] = useState({});
  const [askOff, setAskOff] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/admin/llm/media-models", { params: kind ? { kind } : {} }).then((r) => setD(r.data)).catch((e) => { toast.error(apiErr(e, "Gagal memuat katalog")); setD({ items: [] }); }), [kind]);
  useEffect(() => { setD(null); load(); }, [load]);

  const replace = (item) => setD((x) => ({ ...x, items: x.items.map((m) => (m.id === item.id ? { ...m, ...item } : m)) }));
  const patch = async (m, body, msg) => {
    setBusy(m.id);
    try { const r = await api.patch(`/admin/llm/media-models/${encodeURIComponent(m.id)}`, body); replace(r.data); toast.success(msg); return true; }
    catch (e) { toast.error(apiErr(e, "Gagal menyimpan model")); return false; } finally { setBusy(""); }
  };
  const probe = async (m) => {
    setBusy(`p:${m.id}`);
    try { const r = await api.post(`/admin/llm/media-models/${encodeURIComponent(m.id)}/probe`); setProbes((p) => ({ ...p, [m.id]: r.data })); }
    catch (e) { setProbes((p) => ({ ...p, [m.id]: { ok: false, detail: apiErr(e, "Cek gagal") } })); } finally { setBusy(""); }
  };
  const toggle = (m) => (m.enabled ? setAskOff(m) : patch(m, { enabled: true }, `${m.label} diaktifkan`));

  return (
    <div data-testid="llm-models">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="seg">{KINDS.map(([k, l]) => <button key={k || "all"} onClick={() => setKind(k)} className={`seg-item ${kind === k ? "seg-active" : ""}`} data-testid={`llm-kind-${k || "all"}`}>{l}</button>)}</div>
        {d && <p className="text-xs text-slate-500" data-testid="llm-models-summary">{d.items.filter((m) => m.available).length} dari {d.items.length} model tersedia untuk pengguna</p>}
      </div>
      <div className="mt-3 rounded-xl bg-slate-50 px-4 py-3 text-[11px] text-slate-600" data-testid="llm-quality-info">
        <b>Kredit dasar</b> = kredit pengguna untuk 1 gambar kualitas dasar (Gemini 1K / OpenAI medium 1:1). Pengali kualitas —
        {Object.entries(QUALITY).map(([p, q]) => <span key={p} className="ml-1"><b className="capitalize">{p}</b>: {q.map(([l, x]) => `${l} ×${x}`).join(" / ")}{p === "openai" ? " (+×1.5 non-persegi)" : ""};</span>)}
      </div>
      {!d ? <Loader2 className="mt-6 animate-spin text-slate-400" /> : (
        <>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {d.items.map((m) => <ModelCard key={m.id} m={m} busy={busy} probe={probes[m.id]} fallback={d.emergent_fallback} onToggle={() => toggle(m)} onProbe={() => probe(m)} onEdit={() => setEditing(m)} />)}
          </div>
          {kind !== "image" && <VideoSimulator models={d.items} />}
          {kind !== "image" && <RefLimitsCard models={d.items} />}
        </>
      )}
      <ConfirmDialog open={!!askOff} testid="llm-model-off-confirm" title={`Nonaktifkan ${askOff?.label || "model"}?`} confirmLabel="Ya, nonaktifkan"
        message="Model akan hilang dari pilihan pengguna (chat, workflow, penawaran video). Permintaan gambar ke model ini dialihkan ke model default."
        onCancel={() => setAskOff(null)} onConfirm={() => { const m = askOff; setAskOff(null); patch(m, { enabled: false }, `${m.label} dinonaktifkan`); }} />
      {editing && <PriceDialog m={editing} onClose={() => setEditing(null)} onSave={async (body) => { if (await patch(editing, body, "Harga diperbarui")) setEditing(null); }} busy={busy === editing.id} />}
    </div>
  );
}
